import React, { useState, useEffect, useRef } from 'react';
import { CiFileOn } from "react-icons/ci";
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Alert from 'react-bootstrap/Alert';
import Card from 'react-bootstrap/Card';
import './Dashboard.css';
import streamlineIconLogo from './assets/Navbar_icon_logo.svg';
import { LiaDownloadSolid } from "react-icons/lia";
import { IoIosArrowDown } from "react-icons/io";
// import Sidebar from './SideBar';
import exclamationRedPng from "./assets/exclamation-mark.png";
import exclamationYellowPng from "./assets/warning-sign.png";
import clockPng from "./assets/clock.png";
import calendarPng from "./assets/calender.png";
import pdfIconPng from "./assets/pdf.png";
import grantEngineLogoPng from './assets/Grant Engine Logo Blue.png';
import { generateEditableTDCReport } from "./pdfEditableTemplate";
import TDCReportFormPreview from "./TDCReportFormPreview";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const RESPONSE_FORMAT = process.env.REACT_APP_RESPONSE_FORMAT;

// Helper to load image bytes for PDF
async function loadPngBytes(url) {
  const response = await fetch(url);
  return new Uint8Array(await response.arrayBuffer());
}

const Dashboard = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [, setProjectDescription] = useState('');
  const [nofoNumber, setNofoNumber] = useState('PA-27-100');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [generatedReports, setGeneratedReports] = useState([]);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [isReportReady, setIsReportReady] = useState(false);
  const [reportBlob, setReportBlob] = useState(null);
  const [generatedFileName, setGeneratedFileName] = useState('');
  const [analysisData, setAnalysisData] = useState(null);
  const tdcPreviewRef = useRef();

  // Account settings state
  const [fullName, setFullName] = useState('Jane Doe');
  const [email, setEmail] = useState('jane@email.com');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [alertMsg, setAlertMsg] = useState('');
  const [alertVariant, setAlertVariant] = useState('warning');

  // Fast track and budget period state
  const [fastTrack, setFastTrack] = useState('');
  const [phase1BudgetPeriod, setPhase1BudgetPeriod] = useState([]);

  const [show419Modal, setShow419Modal] = useState(false);
  const [showCapacityModal, setShowCapacityModal] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const [nofoOptions, setNofoOptions] = useState(["Other"]);

  useEffect(() => {
    const fetchNofoNumbers = async () => {
      try {
        const res = await fetch(`${BASE_URL}api/admin/configuration`, { headers: { accept: "application/json" } });
        if (res.ok) {
          const json = await res.json();
          const cfg = json.configData || json;
          const numbers = (cfg.nofoNumbers || []).filter((v) => typeof v === "string" && v.trim());
          setNofoOptions([...numbers, "Other"]);
        }
      } catch (err) {
        console.error("Failed to fetch NOFO numbers:", err);
      }
    };
    fetchNofoNumbers();
  }, []);

  // Auto-dismiss alert after 3 seconds
  useEffect(() => {
    if (alertMsg && (alertVariant === 'warning' || alertVariant === 'success' || alertVariant === 'info')) {
      const timer = setTimeout(() => setAlertMsg(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [alertMsg, alertVariant]);

  useEffect(() => {
    if (fastTrack === "yes") {
      setPhase1BudgetPeriod([1]);
    } else {
      setPhase1BudgetPeriod([]);
    }
  }, [fastTrack]);

  // =========== REPORT GENERATION ===========
  const handleRunReport = async () => {
    if (fastTrack === 'yes' && !phase1BudgetPeriod.includes(1)) {
      setAlertMsg("Select the budget period(s) for phase one.");
      setAlertVariant("warning");
      return;
    }

    setIsGeneratingReport(true);
    setAlertMsg('');

    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, 3600000); // 1 hour

    try {
      const formData = new FormData();
      formData.append(
        'requestData',
        JSON.stringify({
          nofo_number: nofoNumber,
          fast_track: fastTrack === "yes",
          phase1_budget_period: phase1BudgetPeriod,
          response_format: RESPONSE_FORMAT, // "docx" or "json"
        })
      );

      uploadedFiles.forEach((file) => {
        formData.append('documents', file.actualFile || file);
      });

      const response = await fetch(`${BASE_URL}api/process-documents/`, {
        method: 'POST',
        body: formData,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (response.status === 491) {
        setShow419Modal(true);
        setIsGeneratingReport(false);
        return;
      }


      if (response.status === 503) {
        setShowCapacityModal(true);
        setIsGeneratingReport(false);
        return;
      }

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Server error ${response.status}: ${text}`);
      }

      const contentType = response.headers.get('content-type');

      // JSON payload for editable PDF generation (when RESPONSE_FORMAT === "json")
      if (contentType && contentType.includes('application/json') && RESPONSE_FORMAT === 'json') {
        const json = await response.json();
        setAnalysisData(json);
        console.log("raw server json", json.urlanalysis);


        const name = json.file_name || json.filename || 'TDC_Report_Editable.pdf';
        setGeneratedFileName(name);
        setIsReportReady(true);

        setAlertMsg('Report generated successfully!');
        setAlertVariant('success');
        return;
      }

      // DOCX (binary blob directly or via JSON path wrapper)
      let blob;
      let filename;

      if (contentType && contentType.includes('application/json')) {
        // JSON wrapper pointing to a file path
        const responseData = await response.json();
        const filePath = responseData.report_path;

        const downloadUrl = `/api/download-file?path=${encodeURIComponent(filePath)}`;
        const fileResponse = await fetch(BASE_URL + downloadUrl);
        if (!fileResponse.ok) {
          throw new Error('Failed to download the generated file');
        }

        blob = await fileResponse.blob();
        const backendName = filePath.split('/').pop();
        filename =
          backendName && backendName.trim().length > 0
            ? backendName
            : 'report.docx';
      } else {
        // Direct binary response
        blob = await response.blob();
        const headerName =
          response.headers
            .get('Content-Disposition')
            ?.match(/filename="(.+)"/)?.[1] || '';
        filename =
          headerName && headerName.trim().length > 0
            ? headerName
            : 'report.docx';
      }

      setReportBlob(blob);
      setGeneratedFileName(filename);
      setIsReportReady(true);

      const newReport = {
        id: Date.now(),
        name: filename,
        nofoNumber: nofoNumber,
        generatedDate: new Date().toISOString(),
        status: 'completed',
        blob: blob,
      };

      setGeneratedReports((prev) => [...prev, newReport]);
      setAlertMsg('Report generated successfully!');
      setAlertVariant('success');
    } catch (error) {
      console.error('Error generating report:', error);
      if (error.name === 'AbortError') {
        setAlertMsg('Report generation was interrupted. Please try again.');
        setAlertVariant('warning');
      } else if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        setAlertMsg('Unable to connect to the server. Please check your connection and try again.');
        setAlertVariant('danger');
      } else {
        setAlertMsg('Failed to generate report. Please try again.');
        setAlertVariant('danger');
      }
    } finally {
      clearTimeout(timeoutId);
      setIsGeneratingReport(false);
    }
  };

  // =========== DOCX DOWNLOAD (for RESPONSE_FORMAT=docx) ===========
  const handleDownloadReport = async (reportId = null) => {
    try {
      let blobToDownload = reportBlob;
      let filenameToDownload = generatedFileName;

      if (reportId) {
        const report = generatedReports.find(r => r.id === reportId);
        if (report) {
          blobToDownload = report.blob;
          filenameToDownload = report.name;
        }
      }

      if (!blobToDownload) return;

      const url = URL.createObjectURL(blobToDownload);
      const a = document.createElement("a");
      a.href = url;
      a.download = filenameToDownload;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setTimeout(() => {
        setIsReportReady(false);
        setReportBlob(null);
        setGeneratedFileName('');
        setUploadedFiles([]);
        setNofoNumber('');
        setSelectedFile(null);
        setAlertMsg('');
        setAlertVariant('warning');
      }, 1000);
    } catch (error) {
      console.error('Error downloading file:', error);
      setAlertMsg('Failed to download file. Please try again.');
      setAlertVariant('danger');
    }
  };

  // =========== EDITABLE PDF DOWNLOAD (for RESPONSE_FORMAT=json) ===========
  const handleDownloadEditablePDF = async () => {
    if (!analysisData) return;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000);

    try {
      setAlertMsg("Preparing editable PDF download...");
      setAlertVariant("info");

      const pdfBlob = await Promise.race([
        (async () => {
          const exclamationRedBytes = await loadPngBytes(exclamationRedPng);
          const exclamationYellowBytes = await loadPngBytes(exclamationYellowPng);
          const clockBytes = await loadPngBytes(clockPng);
          const calendarBytes = await loadPngBytes(calendarPng);
          const pdfIconBytes = await loadPngBytes(pdfIconPng);
          const logoBytes = await loadPngBytes(grantEngineLogoPng);

          const now = new Date();
          const dateStr = now.toLocaleDateString("en-GB", {
            year: "numeric",
            month: "long",
            day: "numeric"
          });
          const timeStr = now.toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true
          });
          const dateForPDF = `${dateStr}\n${timeStr}`;
          const updatedAnalysisData = { ...analysisData, date: dateForPDF };

          return await generateEditableTDCReport(
            updatedAnalysisData,
            exclamationRedBytes,
            exclamationYellowBytes,
            clockBytes,
            calendarBytes,
            pdfIconBytes,
            logoBytes,
            fastTrack,
            phase1BudgetPeriod
          );
        })(),
        new Promise((_, reject) =>
          controller.signal.addEventListener('abort', () =>
            reject(new Error("Timeout creating PDF"))
          )
        ),
      ]);

      clearTimeout(timeoutId);

      if (pdfBlob) {
        const url = URL.createObjectURL(pdfBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = generatedFileName || 'TDC_Report_Editable.pdf';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        setAlertMsg('Editable PDF downloaded successfully!');
        setAlertVariant('success');

        // Reset after download
        setTimeout(() => {
          setIsReportReady(false);
          setReportBlob(null);
          setAnalysisData(null);
          setGeneratedFileName('');
          setUploadedFiles([]);
          setNofoNumber('');
          setSelectedFile(null);
          setAlertMsg('');
          setAlertVariant('warning');
          setShowModal(false);
        }, 1000);
      } else {
        setAlertMsg('Failed to generate the PDF.');
        setAlertVariant('danger');
      }
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError' || error.message.includes("Timeout")) {
        setAlertMsg('PDF generation took too long (timed out). Please try again.');
        setAlertVariant('warning');
      } else {
        setAlertMsg('Failed to generate or download the PDF. Please try again.');
        setAlertVariant('danger');
        console.error('Error generating editable PDF:', error);
      }
    }
  };

  // =========== FILE UPLOAD HANDLERS ===========
  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files).filter(file => file.type === "application/pdf");
    const existingNames = uploadedFiles.map(f => f.name);
    const newFiles = files.filter(file => !existingNames.includes(file.name));

    if (uploadedFiles.length + newFiles.length > 3) {
      setAlertMsg("You can only upload up to 3 PDF files.");
      setAlertVariant("warning");
      event.target.value = "";
      return;
    }

    if (newFiles.length < files.length) {
      setAlertMsg("Duplicate files are not allowed.");
      setAlertVariant("warning");
      event.target.value = "";
      return;
    }

    const filesToAdd = newFiles.map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
      uploadDate: new Date().toISOString(),
      actualFile: file
    }));

    setUploadedFiles(prevFiles => [...prevFiles, ...filesToAdd]);
    event.target.value = "";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files).filter(file => file.type === "application/pdf");

    if (files.length === 0) {
      setAlertMsg("Please drop only PDF files.");
      setAlertVariant("warning");
      return;
    }

    const existingNames = uploadedFiles.map(f => f.name);
    const newFiles = files.filter(file => !existingNames.includes(file.name));

    if (uploadedFiles.length + newFiles.length > 3) {
      setAlertMsg("You can only upload up to 3 PDF files.");
      setAlertVariant("warning");
      return;
    }

    if (newFiles.length < files.length) {
      setAlertMsg("Duplicate files are not allowed.");
      setAlertVariant("warning");
      return;
    }

    const filesToAdd = newFiles.map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
      uploadDate: new Date().toISOString(),
      actualFile: file
    }));

    setUploadedFiles(prevFiles => [...prevFiles, ...filesToAdd]);

    if (filesToAdd.length > 0) {
      setSelectedFile(filesToAdd[0].actualFile);
    }
  };

  const handleRemoveFile = (fileId) => {
    setUploadedFiles(prevFiles => prevFiles.filter(file => file.id !== fileId));
    const removedFile = uploadedFiles.find(file => file.id === fileId);
    if (removedFile && selectedFile && selectedFile.name === removedFile.name) {
      setSelectedFile(null);
    }
  };

  // =========== MODAL HANDLERS ===========
  const handleCloseModal = () => {
    setShowModal(false);
    setProjectName('');
    setProjectDescription('');
  };

  const handleSaveProject = () => {
    handleCloseModal();
  };

  const handleCloseAccountModal = () => {
    setShowAccountModal(false);
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleSaveAccount = () => {
    handleCloseAccountModal();
  };

  const handleLogout = () => {
    handleCloseAccountModal();
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  // =========== RENDER ===========
  return (
    <div className="dashboard-container">
      {alertMsg && (
        <div className="top-alert">
          <Alert
            variant={alertVariant}
            onClose={() => setAlertMsg('')}
            dismissible
            style={{ margin: 0, minWidth: 480, maxWidth: '90vw', fontSize: 15, padding: '12px 18px' }}
          >
            {alertMsg}
          </Alert>
        </div>
      )}

      {/* <Sidebar
        onDashboardClick={() => window.location.pathname = '/dashboard'}
        // onAdminPanelClick={() => window.location.pathname = '/admin'}
        // isAdminPanelActive={false}
      /> */}

      <div className="main-content">
        <div className="dashboard-header">
          <img src={streamlineIconLogo} alt="Streamline Icon" className="dashboard-header-logo" />
          <span className="dashboard-header-title">
            NIH SBIR/STTR Technical Document Checker
          </span>
        </div>

        <div className="generate-report-outer-box">
          {/* Fast Track Section */}
          <Card
            className="mb-4 w-100"
            style={{
              backgroundColor: 'transparent',
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              boxShadow: 'none',
              fontFamily: 'inherit',
              fontSize: 14,
              fontWeight: 400
            }}
          >
            <Card.Body>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 18,
                  marginBottom: 6,
                  fontFamily: 'inherit',
                  fontSize: 14,
                  fontWeight: 600
                }}
              >
                <span>Is the application Fast Track?</span>
                <Form.Check
                  inline
                  type="radio"
                  label="Yes"
                  name="fastTrack"
                  id="fastTrackYes"
                  value="yes"
                  checked={fastTrack === 'yes'}
                  onChange={e => setFastTrack(e.target.value)}
                  style={{ fontFamily: 'inherit', fontSize: 14, fontWeight: 400, marginBottom: 0 }}
                />
                <Form.Check
                  inline
                  type="radio"
                  label="No"
                  name="fastTrack"
                  id="fastTrackNo"
                  value="no"
                  checked={fastTrack === 'no'}
                  onChange={e => setFastTrack(e.target.value)}
                  style={{ fontFamily: 'inherit', fontSize: 14, fontWeight: 400, marginBottom: 0 }}
                />
              </div>

              {fastTrack === 'yes' && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 18,
                    marginTop: 12,
                    fontFamily: 'inherit',
                    fontSize: 14,
                    fontWeight: 600
                  }}
                >
                  <span style={{ minWidth: 140 }}>What are the phase one budget period(s)?</span>
                  <Form.Check
                    type="checkbox"
                    label="1"
                    name="phase1BudgetPeriod"
                    id="phase1BudgetPeriod1"
                    checked={phase1BudgetPeriod.includes(1)}
                    onChange={e => {
                      if (e.target.checked) {
                        setPhase1BudgetPeriod([1]);
                      } else {
                        setPhase1BudgetPeriod([]);
                      }
                    }}
                    style={{
                      fontFamily: 'inherit',
                      fontSize: 14,
                      fontWeight: 400
                    }}
                  />
                  <Form.Check
                    type="checkbox"
                    label="2"
                    name="phase1BudgetPeriod"
                    id="phase1BudgetPeriod2"
                    checked={phase1BudgetPeriod.includes(2)}
                    onChange={e => {
                      if (e.target.checked) {
                        if (!phase1BudgetPeriod.includes(1)) {
                          setAlertMsg("Please select 1 before selecting 2.");
                          setAlertVariant("warning");
                          return;
                        }
                        setPhase1BudgetPeriod([1, 2, ...(phase1BudgetPeriod.includes(3) ? [3] : [])]);
                      } else {
                        setPhase1BudgetPeriod(phase1BudgetPeriod.filter(x => x !== 2 && x !== 3));
                      }
                    }}
                    disabled={!phase1BudgetPeriod.includes(1)}
                    style={{
                      fontFamily: 'inherit',
                      fontSize: 14,
                      fontWeight: 400
                    }}
                  />
                  <Form.Check
                    type="checkbox"
                    label="3"
                    name="phase1BudgetPeriod"
                    id="phase1BudgetPeriod3"
                    checked={phase1BudgetPeriod.includes(3)}
                    onChange={e => {
                      if (e.target.checked) {
                        if (!phase1BudgetPeriod.includes(1) || !phase1BudgetPeriod.includes(2)) {
                          setAlertMsg("Please select both 1 and 2 before selecting 3.");
                          setAlertVariant("warning");
                          return;
                        }
                        setPhase1BudgetPeriod([1, 2, 3]);
                      } else {
                        setPhase1BudgetPeriod(phase1BudgetPeriod.filter(x => x !== 3));
                      }
                    }}
                    disabled={!phase1BudgetPeriod.includes(1) || !phase1BudgetPeriod.includes(2)}
                    style={{
                      fontFamily: 'inherit',
                      fontSize: 14,
                      fontWeight: 400
                    }}
                  />
                </div>
              )}
            </Card.Body>
          </Card>

          {/* NOFO Number Input */}
          <div className="nofo-input-section">
            <div className="nofo-select-container">
              <select
                value={nofoNumber}
                onChange={(e) => setNofoNumber(e.target.value)}
                className="nofo-select"
                disabled={isGeneratingReport}
              >
                <option value="" disabled>
                  Select NOFO Number*
                </option>
                {nofoOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <IoIosArrowDown className="nofo-select-icon" />
            </div>
          </div>

          {/* Upload Section */}
          <div className="upload-section">
            <div className="upload-row">
              <div
                className="upload-area"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <div className="upload-content">
                  <div className="upload-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M7 18a4.6 4.4 0 0 1 0 -9a5 4.5 0 0 1 11 2h1a3.5 3.5 0 0 1 0 7h-1" />
                      <polyline points="9,15 12,12 15,15" />
                      <path d="m12 12v9" />
                    </svg>
                  </div>
                  <p>Click to upload a document or drag-and-drop</p>
                  <input
                    type="file"
                    onChange={handleFileUpload}
                    className="file-input"
                    accept=".pdf"
                    multiple
                    disabled={isGeneratingReport}
                  />
                </div>
              </div>

              {uploadedFiles.length > 0 && (
                <div className="uploaded-files-square-box">
                  <div className="files-header">
                    <span className="files-count">
                      {uploadedFiles.length} file{uploadedFiles.length > 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="files-list">
                    {uploadedFiles.map((file) => (
                      <div key={file.id} className="file-item">
                        <div className="file-details" title={file.name}>
                          <CiFileOn style={{ fontSize: '18px', color: '#9ca3af' }} />
                          <span className="file-name">{file.name}</span>
                        </div>
                        <button
                          className="remove-file-btn"
                          onClick={() => handleRemoveFile(file.id)}
                          title="Remove file"
                          disabled={isGeneratingReport}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Analyzing indicator */}
          {isGeneratingReport && (
            <div className="analyzing-indicator">
              <div className="spinner"></div>
              <span>Analyzing<span className="thinking-dots"><span>.</span><span>.</span><span>.</span></span></span>
            </div>
          )}

          {/* Button Section */}
          <div className="button-section report-action-row">
            <span className="generated-report-filename">
              {isReportReady ? generatedFileName : ''}
            </span>
            <div style={{ position: "relative", display: "inline-block" }}>
              {!isReportReady ? (
                <button
                  className="run-report-button"
                  onClick={handleRunReport}
                  disabled={
                    fastTrack === "" ||
                    (fastTrack === "yes" && phase1BudgetPeriod.length === 0) ||
                    !nofoNumber.trim() ||
                    uploadedFiles.length < 1 ||
                    uploadedFiles.length > 3 ||
                    isGeneratingReport
                  }
                  onMouseEnter={() => {
                    if (uploadedFiles.length < 1 || uploadedFiles.length > 3) setShowTooltip(true);
                  }}
                  onMouseLeave={() => setShowTooltip(false)}
                >
                  {isGeneratingReport ? (
                    <>
                      <div className="spinner"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <svg className="run-report-icon" viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Run Report
                    </>
                  )}
                </button>
              ) : (
                <button
                  className="run-report-button"
                  onClick={() => {
                    if (RESPONSE_FORMAT === 'json') {
                      setShowModal(true);
                    } else {
                      handleDownloadReport();
                    }
                  }}
                >
                  <LiaDownloadSolid style={{ fontSize: 18, marginRight: 6 }} />
                  {RESPONSE_FORMAT === 'json' ? 'View & Download Report' : 'Download Report'}
                </button>
              )}
              {showTooltip && (uploadedFiles.length < 1 || uploadedFiles.length > 3) && !isReportReady && (
                <div className="run-report-tooltip">
                  Upload 1 to 3 files (Application required; PHS Assignment and DMSP optional)
                </div>
              )}
            </div>
          </div>
        </div>

      </div>

      {/* Project Modal */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        centered
        dialogClassName="custom-project-modal"
      >
        <Modal.Body className="custom-modal-body">
          <div className="modal-content-wrapper">
            <h4 className="modal-title">Project Name</h4>
            <Form.Control
              type="text"
              placeholder="e.g. Acme Co"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              className="project-name-input"
              autoFocus
            />
            <div className="info-section">
              <div className="info-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M9,9h0a3,3,0,0,1,6,0c0,2-3,3-3,3" />
                  <path d="M12,17h.01" />
                </svg>
              </div>
              <div className="info-text">
                <div className="info-title">What's a project?</div>
                <div className="info-description">
                  It's a place to organize chats, files, and settings for easy access and ongoing work.
                </div>
              </div>
            </div>
            <div className="modal-buttons">
              <Button
                variant="outline-secondary"
                onClick={handleCloseModal}
                className="cancel-button"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveProject}
                disabled={!projectName.trim()}
                className="add-project-button"
              >
                Add Project
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* 491 Error Modal */}
      <Modal
        show={show419Modal}
        onHide={() => setShow419Modal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Incorrect Application Type</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Application type mismatch detected: the grant type selected does not match the uploaded application. Please update your selection (or upload the matching documents) and try again.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 503 Error Modal - System at Capacity */}
      <Modal
        show={showCapacityModal}
        onHide={() => setShowCapacityModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>System at Capacity</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            The system is currently processing other documents and has reached its API capacity. Please try again after the current batch completes.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowCapacityModal(false)}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Report Preview Modal (for JSON/PDF format) */}
      <Modal
        show={showModal && RESPONSE_FORMAT === 'json'}
        onHide={() => setShowModal(false)}
        size="lg"
        aria-labelledby="tdc-modal-title"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="tdc-modal-title">TDC Report Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            background: "#f6f9fa",
            maxHeight: "70vh",
            overflowY: "auto",
            paddingBottom: "80px"
          }}
        >
          <div ref={tdcPreviewRef}>
            {analysisData && (() => {
              const now = new Date();
              const dateStr = now.toLocaleDateString("en-GB", {
                year: "numeric",
                month: "long",
                day: "numeric"
              });
              const timeStr = now.toLocaleTimeString("en-GB", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true
              });
              const dateForPDF = `${dateStr}\n${timeStr}`;
              const previewData = { ...analysisData, date: dateForPDF };

              return (
                <TDCReportFormPreview
                  data={previewData}
                  fastTrack={fastTrack}
                  phase1BudgetPeriod={phase1BudgetPeriod}
                />
              );
            })()}
          </div>
        </Modal.Body>
        <Modal.Footer
          style={{
            position: "sticky",
            bottom: 0,
            background: "#fff",
            borderTop: "1px solid #ddd",
            zIndex: 10,
            display: "flex",
            justifyContent: "flex-end"
          }}
        >
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
          <Button
            className="run-report-button"
            style={{ fontWeight: 600, fontSize: 14, minWidth: 180 }}
            onClick={handleDownloadEditablePDF}
          >
            <LiaDownloadSolid style={{ fontSize: 18, marginRight: 6 }} />
            Download Editable PDF
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Account Settings Modal */}
      {showAccountModal && (
        <div className="custom-modal-overlay" onClick={handleCloseAccountModal}>
          <div className="custom-account-modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="custom-account-modal-content">
              <div className="account-modal-header">
                <h4 className="account-modal-title">Account Settings</h4>
                <button
                  className="custom-close-button"
                  onClick={handleCloseAccountModal}
                  aria-label="Close"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>

              <div className="account-modal-body">
                <div className="account-profile-section">
                  <div className="account-avatar-section">
                    <div className="account-avatar">
                      <img
                        src={require('./assets/user-icon-on-transparent-background-free-png.webp')}
                        alt="Profile"
                      />
                    </div>
                  </div>
                  <div className="account-form">
                    <div className="form-row">
                      <label className="form-label-left">Full Name:</label>
                      <input
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        className="account-input-inline"
                        placeholder="e.g. Jane Doe"
                      />
                    </div>

                    <div className="form-row">
                      <label className="form-label-left">Email:</label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="account-input-inline"
                        placeholder="e.g. jane@email.com"
                      />
                    </div>

                    <div className="password-section">
                      <h6 className="password-title">Reset Password</h6>
                      <div className="form-row">
                        <label className="form-label-left">New Password:</label>
                        <input
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="account-input-inline"
                        />
                      </div>
                      <div className="form-row">
                        <label className="form-label-left">Confirm Password:</label>
                        <input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="account-input-inline"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="account-modal-footer">
                <div className="footer-left-section">
                  <button className="logout-button" onClick={handleLogout}>
                    <svg className="logout-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16,17 21,12 16,7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    Logout
                  </button>
                </div>
                <div className="footer-right-section">
                  <button className="account-cancel-btn" onClick={handleCloseAccountModal}>
                    Cancel
                  </button>
                  <button onClick={handleSaveAccount} className="account-save-btn">
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
